import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { getUserInfo, getStorage, STORAGE_KEYS } from '@shared/api/client';
import { useLanguage } from '@shared/contexts/LanguageContext';
import { getMyProfile } from '@features/profile/api/profileService';
import { geContentList } from '../api/wishTreeService';
import { CARD_STYLES } from '../constants';

function buildVowerOptions({ name, dharma, temple }) {
    const opts = [];
    const trimmedTemple = temple?.trim() || '';
    const t = trimmedTemple && trimmedTemple !== '無' && trimmedTemple.toUpperCase() !== 'N/A' ? trimmedTemple : '';
    const n = name?.trim() || '';
    const d = dharma?.trim() || '';
    if (n) opts.push({ value: n, label: n, vower_name: n, monastery_name: '' });
    if (d) opts.push({ value: d, label: d, vower_name: d, monastery_name: '' });
    if (t && n) opts.push({ value: `${t}‧${n}`, label: `${t}‧${n}`, vower_name: n, monastery_name: t });
    if (t && d) opts.push({ value: `${t}‧${d}`, label: `${t}‧${d}`, vower_name: d, monastery_name: t });
    if (d && n) opts.push({ value: `${d}‧${n}`, label: `${d}‧${n}`, vower_name: `${d}‧${n}`, monastery_name: '' });
    return opts;
}

/**
 * 發願表單 Popup — 發願者 / 卡片樣式（四選一）/ 發願文字（六選一）
 */
const WishFormModal = ({ onClose, onSubmit, submitting }) => {
    const { t, currentLanguage } = useLanguage();
    const isEn = currentLanguage.code === 'en';
    const [imageId, setImageId] = useState(CARD_STYLES[0].id);
    const user = getUserInfo();
    const isMemberLoggedIn = !!getStorage(STORAGE_KEYS.MEMBER_TOKEN);

    const [profileName, setProfileName] = useState(user?.name ?? '');
    const [dharmaName, setDharmaName] = useState(user?.dharmaName ?? '');
    const [monasteryName, setMonasteryName] = useState(user?.monasteryName ?? '');
    const [selectedVower, setSelectedVower] = useState(user?.name ?? user?.dharmaName ?? '');
    const [guestName, setGuestName] = useState('');

    const [contentId, setContentId] = useState(null);
    const [wishOptions, setWishOptions] = useState([]);
    const [wishOptionsLoading, setWishOptionsLoading] = useState(true);
    const [errors, setErrors] = useState({});
    const dropdownRef = useRef(null);
    const [dropdownOpen, setDropdownOpen] = useState(false);

    const vowerOptions = useMemo(
        () => buildVowerOptions({ name: profileName, dharma: dharmaName, temple: monasteryName }),
        [profileName, dharmaName, monasteryName],
    );

    const showVowerDropdown = isMemberLoggedIn && vowerOptions.length > 1;
    const selectedVowerOption = vowerOptions.find((opt) => opt.value === selectedVower) ?? vowerOptions[0];

    useEffect(() => {
        setWishOptionsLoading(true);
        geContentList()
            .then((res) => {
                if (res?.items?.length > 0) {
                    setWishOptions(res.items);
                }
            })
            .catch(() => {})
            .finally(() => setWishOptionsLoading(false));
    }, []);

    useEffect(() => {
        if (!isMemberLoggedIn) return;
        getMyProfile()
            .then((profile) => {
                if (!profile) return;
                setProfileName(profile.user_name ?? user?.name ?? '');
                setDharmaName(profile.dharma_name ?? user?.dharmaName ?? '');
                setMonasteryName(profile.monastery_name ?? user?.monasteryName ?? '');
            })
            .catch(() => {});
    }, [isMemberLoggedIn]);

    useEffect(() => {
        if (vowerOptions.length === 0) return;
        if (!vowerOptions.find((opt) => opt.value === selectedVower)) {
            setSelectedVower(vowerOptions[0].value);
        }
    }, [vowerOptions, selectedVower]);

    const handleSubmit = () => {
        const nextErrors = {};
        if (isMemberLoggedIn) {
            if (!selectedVowerOption) nextErrors.vower = true;
        } else if (!guestName.trim()) {
            nextErrors.vower = true;
        }
        if (!contentId) nextErrors.content = true;
        if (Object.keys(nextErrors).length > 0) {
            setErrors(nextErrors);
            return;
        }

        onSubmit({
            vower_name: isMemberLoggedIn
                ? (selectedVowerOption?.label || selectedVowerOption?.vower_name || '')
                : guestName.trim(),
            image_id: imageId,
            content_id: contentId,
        });
    };

    useEffect(() => {
        if (!dropdownOpen) return;
        function handler(e) {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdownOpen(false);
        }
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [dropdownOpen]);

    return (
        <div className="fixed inset-0 z-500 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}>
            <div className="w-full max-w-[440px] max-h-[90vh] overflow-y-auto rounded-2xl bg-[#FFFBF3] relative shadow-xl">
                <button
                    onClick={onClose}
                    className="absolute top-3 right-3 z-10 w-9 h-9 flex items-center justify-center rounded-full bg-black/10 hover:bg-black/20 transition-colors"
                    aria-label={t('wish-tree-page.closeAria')}
                >
                    <X size={18} />
                </button>

                <div className="px-6 py-8">
                    <h2
                        className={`${isEn ? 'text-xl' : 'text-2xl'} font-bold text-center mb-1`}
                        style={{ color: '#5A3B22' }}
                    >
                        {t('wish-tree-page.formTitle')}
                    </h2>

                    {/* 發願者 */}
                    <div className="mb-5">
                        <label className="block text-sm font-medium mb-2" style={{ color: '#5A3B22' }}>
                            {t('wish-tree-page.vowerLabel')}
                        </label>
                        {showVowerDropdown ? (
                            <div ref={dropdownRef} className="relative">
                                <button
                                    type="button"
                                    className="w-full px-4 py-3 rounded-lg border border-[#D8C4A8] bg-white text-sm text-left flex items-center justify-between"
                                    style={{ color: '#3E2418', boxShadow: '1px 2px 12px rgba(0,0,0,0.04)' }}
                                    onClick={() => setDropdownOpen((o) => !o)}
                                    aria-expanded={dropdownOpen}
                                >
                                    <span className="overflow-hidden text-ellipsis whitespace-nowrap">
                                        {selectedVowerOption?.label || t('wish-tree-page.yourName')}
                                    </span>
                                    <svg
                                        viewBox="0 0 10 6"
                                        style={{
                                            width: 'clamp(8px, 2vw, 13px)',
                                            height: 'clamp(5px, 1.3vw, 8px)',
                                            flexShrink: 0,
                                            marginLeft: 4,
                                            transition: 'transform 0.2s',
                                            transform: dropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                            fill: 'none',
                                            stroke: '#9E6D61',
                                            strokeWidth: 1.8,
                                            strokeLinecap: 'round',
                                            strokeLinejoin: 'round',
                                        }}
                                    >
                                        <path d="M1 1l4 4 4-4" />
                                    </svg>
                                </button>
                                {dropdownOpen && (
                                    <div
                                        className="absolute left-0 right-0 top-full mt-1 rounded-lg overflow-hidden z-50"
                                        style={{
                                            background: '#FFFDF9',
                                            border: '1px solid #E8CFC0',
                                            boxShadow: '0 4px 20px rgba(62,36,24,0.15)',
                                        }}
                                    >
                                        {vowerOptions.map((opt) => {
                                            const active = opt.value === selectedVower;
                                            return (
                                                <button
                                                    key={opt.value}
                                                    type="button"
                                                    onMouseDown={(e) => {
                                                        e.preventDefault();
                                                        setSelectedVower(opt.value);
                                                        setDropdownOpen(false);
                                                        setErrors((p) => ({ ...p, vower: false }));
                                                    }}
                                                    className="w-full text-left px-4 py-2.5 text-sm cursor-pointer flex items-center gap-2"
                                                    style={{
                                                        fontWeight: active ? 500 : 300,
                                                        color: active ? '#5A2A23' : '#6E4C3E',
                                                        background: active ? 'rgba(198,125,93,0.08)' : 'transparent',
                                                        borderBottom: '1px solid #F5E8DF',
                                                    }}
                                                >
                                                    {active ? (
                                                        <svg
                                                            viewBox="0 0 10 8"
                                                            style={{
                                                                width: 10,
                                                                flexShrink: 0,
                                                                fill: 'none',
                                                                stroke: '#C67D5D',
                                                                strokeWidth: 2,
                                                                strokeLinecap: 'round',
                                                                strokeLinejoin: 'round',
                                                            }}
                                                        >
                                                            <path d="M1 4l3 3 5-6" />
                                                        </svg>
                                                    ) : (
                                                        <span style={{ width: 10, flexShrink: 0 }} />
                                                    )}
                                                    {opt.label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        ) : isMemberLoggedIn ? (
                            <div
                                className="w-full px-4 py-3 rounded-lg border border-[#D8C4A8] bg-white text-sm"
                                style={{ color: '#3E2418' }}
                            >
                                {selectedVowerOption?.label || profileName.trim() || dharmaName.trim() || t('wish-tree-page.yourName')}
                            </div>
                        ) : (
                            <input
                                className="w-full px-4 py-3 rounded-lg border border-[#D8C4A8] bg-white text-sm"
                                placeholder={t('wish-tree-page.namePlaceholder')}
                                value={guestName}
                                maxLength={30}
                                onChange={(e) => {
                                    setGuestName(e.target.value);
                                    setErrors((p) => ({ ...p, vower: false }));
                                }}
                            />
                        )}

                        {errors.vower && (
                            <p className="mt-1 text-xs text-red-500">{t('wish-tree-page.vowerRequired')}</p>
                        )}
                    </div>

                    {/* 卡片樣式 四選一 */}
                    <div className="mb-5">
                        <label className="block text-sm font-medium mb-2" style={{ color: '#5A3B22' }}>
                            {t('wish-tree-page.cardStyleLabel')}
                        </label>
                        <div className="grid grid-cols-4 gap-2">
                            {CARD_STYLES.map((style) => (
                                <button
                                    key={style.id}
                                    type="button"
                                    onClick={() => setImageId(style.id)}
                                    className="relative rounded-md overflow-hidden border-2 transition-all"
                                    style={{
                                        aspectRatio: '2 / 3',
                                        borderColor: imageId === style.id ? '#B8860B' : 'transparent',
                                        boxShadow: imageId === style.id ? '0 0 0 2px rgba(184,134,11,0.3)' : 'none',
                                    }}
                                >
                                    <img src={style.image} alt="" className="absolute inset-0 w-full h-full object-cover" />
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* 發願文字 */}
                    <div className="mb-6">
                        <label className="block text-sm font-medium mb-2" style={{ color: '#5A3B22' }}>
                            {t('wish-tree-page.wishTextLabel')}
                        </label>
                        <div className="flex flex-col gap-2" aria-busy={wishOptionsLoading}>
                            {wishOptionsLoading
                                ? Array.from({ length: 6 }, (_, i) => (
                                    <div
                                        key={i}
                                        className="flex items-center gap-3 px-4 py-3 rounded-lg border"
                                        style={{
                                            borderColor: '#D8C4A8',
                                            backgroundColor: '#FFFFFF',
                                            minHeight: '3.25rem',
                                        }}
                                        aria-hidden
                                    >
                                        <div
                                            className="w-5 h-5 rounded-full border shrink-0"
                                            style={{ borderColor: '#D8C4A8' }}
                                        />
                                        <div
                                            className="h-3.5 rounded flex-1 max-w-[70%]"
                                            style={{ backgroundColor: 'rgba(216,196,168,0.45)' }}
                                        />
                                    </div>
                                ))
                                : wishOptions.map((wish) => {
                                    const isSelected = contentId === wish.content_ind;
                                    return (
                                        <div
                                            key={wish.content_ind}
                                            className="flex items-center gap-3 px-4 py-3 rounded-lg border transition-colors cursor-pointer select-none"
                                            role="radio"
                                            aria-checked={isSelected}
                                            tabIndex={0}
                                            onClick={() => {
                                                setContentId(wish.content_ind);
                                                setErrors((p) => ({ ...p, content: false }));
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    setContentId(wish.content_ind);
                                                    setErrors((p) => ({ ...p, content: false }));
                                                }
                                            }}
                                            style={{
                                                borderColor: isSelected ? '#B8860B' : '#D8C4A8',
                                                backgroundColor: isSelected ? 'rgba(184,134,11,0.08)' : '#FFFFFF',
                                            }}
                                        >
                                            <div
                                                className="w-5 h-5 rounded-full border flex items-center justify-center transition-colors"
                                                style={{
                                                    borderColor: isSelected ? '#B8860B' : '#D8C4A8',
                                                    backgroundColor: isSelected ? '#B8860B' : 'transparent',
                                                }}
                                            >
                                                {isSelected && <div className="w-2 h-2 rounded-full bg-white" />}
                                            </div>
                                            <span
                                                className="text-sm font-medium whitespace-pre-wrap"
                                                style={{ color: '#5A3B22' }}
                                            >
                                                {wish.content}
                                            </span>
                                        </div>
                                    );
                                })}
                        </div>

                        <div className="flex items-center justify-between mt-1">
                            {errors.content ? (
                                <p className="text-xs text-red-500">{t('wish-tree-page.wishTextRequired')}</p>
                            ) : (
                                <span />
                            )}
                        </div>
                    </div>

                    <button
                        onClick={handleSubmit}
                        disabled={submitting}
                        className="w-full py-3 rounded-full font-semibold text-white transition-opacity disabled:opacity-60"
                        style={{ background: 'linear-gradient(135deg, #C9942B, #A8721E)' }}
                    >
                        {submitting ? t('wish-tree-page.submitting') : t('wish-tree-page.submit')}
                    </button>
                </div>
            </div>
        </div>
    );
};

WishFormModal.propTypes = {
    onClose: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    submitting: PropTypes.bool,
};

export default WishFormModal;
